// Cola de acciones hechas sin conexión. Se guarda en IndexedDB y se aplica en orden al
// reconectar: una falla de red detiene la cola (se reintenta después); un rechazo del
// servidor descarta esa acción y se informa.

import { isNetworkError } from './errors.ts';
import type { CardChanges } from '@shared/notion/properties.ts';
import type { Profile } from './types.ts';

interface Base {
  /** Id de la acción en la cola. */
  qid: string;
  /** Instante real en que el usuario hizo la acción. */
  at: string;
}

export type QueuedAction =
  | (Base & { kind: 'update-card'; card_id: string; changes: CardChanges })
  | (Base & { kind: 'start-timer'; card_id: string })
  | (Base & { kind: 'stop-timer' })
  // Fijan o borran el registro de (persona, tarjeta, día): repetirlas da el mismo resultado.
  | (Base & {
      kind: 'set-work-log';
      log: { card_id: string; work_date: string; seconds: number; user_id?: string };
    })
  | (Base & {
      kind: 'delete-work-log';
      log: { card_id: string; work_date: string; user_id?: string };
    })
  | (Base & { kind: 'set-pref'; patch: Partial<Pick<Profile, 'solo_lo_mio' | 'redondeo'>> });

export type NewAction = QueuedAction extends infer A
  ? A extends QueuedAction
    ? Omit<A, 'qid' | 'at'> & { at?: string }
    : never
  : never;

export interface QueueStorage {
  load(): Promise<QueuedAction[]>;
  save(actions: QueuedAction[]): Promise<void>;
}

export interface FlushResult {
  applied: QueuedAction[];
  rejected: { action: QueuedAction; error: unknown }[];
  remaining: number;
}

export type Executor = (action: QueuedAction) => Promise<void>;

export class ActionQueue {
  private actions: QueuedAction[] = [];
  private loaded: Promise<void>;
  private chain: Promise<unknown> = Promise.resolve();
  private listeners = new Set<(pending: number) => void>();

  constructor(
    private readonly storage: QueueStorage,
    private readonly execute: Executor,
    private readonly newId: () => string = () => crypto.randomUUID(),
  ) {
    this.loaded = storage.load().then((list) => {
      this.actions = list;
      this.emit();
    });
  }

  get pending(): number {
    return this.actions.length;
  }

  list(): readonly QueuedAction[] {
    return this.actions;
  }

  subscribe(listener: (pending: number) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit() {
    for (const l of this.listeners) l(this.actions.length);
  }

  /** Serializa operaciones para que nunca haya dos vaciados en paralelo. */
  private serial<T>(fn: () => Promise<T>): Promise<T> {
    const next = this.chain.then(fn, fn);
    this.chain = next.catch(() => undefined);
    return next;
  }

  async enqueue(action: NewAction): Promise<QueuedAction> {
    await this.loaded;
    return this.serial(async () => {
      const full = {
        ...action,
        qid: this.newId(),
        at: action.at ?? new Date().toISOString(),
      } as QueuedAction;
      this.actions = [...this.actions, full];
      await this.storage.save(this.actions);
      this.emit();
      return full;
    });
  }

  async flush(): Promise<FlushResult> {
    await this.loaded;
    return this.serial(async () => {
      const result: FlushResult = { applied: [], rejected: [], remaining: 0 };
      while (this.actions.length > 0) {
        const [action] = this.actions;
        if (!action) break;
        try {
          await this.execute(action);
          result.applied.push(action);
        } catch (error) {
          if (isNetworkError(error)) break;
          result.rejected.push({ action, error });
        }
        this.actions = this.actions.slice(1);
        await this.storage.save(this.actions);
        this.emit();
      }
      result.remaining = this.actions.length;
      return result;
    });
  }

  async clear(): Promise<void> {
    await this.loaded;
    this.actions = [];
    await this.storage.save([]);
    this.emit();
  }
}
